import { Component, Host, h, Prop, Event, EventEmitter, Element, Method, State } from '@stencil/core';
import { Attributes, inheritAriaAttributes, observeAriaAttributes } from '../../utils';
import { getFormFieldId, connectStopPropagateFormEvents, disconnectStopPropagateFormEvents } from '../../utils/form.util';

let id = 0;

/**
 * @slot - Radio text content
 *
 * @part label - The component's label wrapper element.
 * @part input - The component's input element.
 * @part text - The component's text content element.
 */
@Component({
  tag: 'o2-radio',
  styleUrl: 'radio.scss',
  scoped: true,
})
export class O2Radio {
  private input?: HTMLInputElement;
  private inputId = `o2-radio-${++id}`;

  private hasTextSlot: boolean;

  private cleanupListeners: (event: Event) => void;

  /**
   * Instance of MutationObserver
   */
  private mutationObserver: MutationObserver;

  /**
   * Inherited attributes from the host element.
   */
  @State() inheritedAttributes: Attributes = {};

  @Element() el!: HTMLO2RadioElement;

  /**
   * Indicates whether the input allows propagation of native events.
   */
  @Prop() native?: boolean = false;

  /**
   * Sets the value of radio.
   */
  @Prop({ mutable: true, reflect: true }) value: string;

  /**
   * Sets the name of radio input.
   */
  @Prop() name?: string;

  /**
   * turns on/off the checked state of radio.
   */
  @Prop({ mutable: true, reflect: true }) checked?: boolean = false;

  /**
   * Disables radio.
   */
  @Prop({ reflect: true }) disabled?: boolean = false;

  /**
   * If `true`, the user must fill in a value before submitting a form.
   */
  @Prop() required? = false;

  /**
   * Indicates error
   */
  @Prop({
    reflect: true,
  })
  error?: boolean = false;

  /**
   * Emits radio change.
   */
  @Event() o2RadioChange!: EventEmitter<any>;

  /**
   * Emitted when the input is ready.
   */
  @Event() o2Ready!: EventEmitter<Event>;

  /**
   * Sets focus on the native `input` in `o2-radio`. Use this method instead of the global
   * `input.focus()`.
   */
  @Method()
  async setFocus() {
    if (this.input) {
      this.input.focus();
    }
  }

  /**
   * Sets blur on the native `input` in `o2-radio`. Use this method instead of the global
   * `input.blur()`.
   * @internal
   */
  @Method()
  async setBlur() {
    if (this.input) {
      this.input.blur();
    }
  }

  /**
   * Fires on component change
   */
  private onChange = () => {
    this.value = this.el.querySelector('input').value;

    this.o2RadioChange.emit({
      value: this.value == null ? this.value : this.value.toString(),
    });
  };

  connectedCallback() {
    // check if wc has at least one node
    this.hasTextSlot = !!this.el.childNodes.length;

    this.mutationObserver = observeAriaAttributes(this.el as HTMLElement, () => {
      // merge attributes
      const newAttributes = inheritAriaAttributes(this.el as HTMLElement);
      this.inheritedAttributes = { ...this.inheritedAttributes, ...newAttributes };
    });
  }

  componentWillLoad() {
    this.inheritedAttributes = inheritAriaAttributes(this.el as HTMLElement);
  }

  componentDidRender() {
    this.o2Ready.emit();
  }

  componentDidLoad() {
    const inputElement = this.el.querySelector('input');

    if (!this.native) {
      this.cleanupListeners = connectStopPropagateFormEvents(inputElement);
    }
  }

  disconnectedCallback() {
    // Remove event listeners
    if (!this.native && this.cleanupListeners) {
      const inputElement = this.el.querySelector('input');
      disconnectStopPropagateFormEvents(inputElement, this.cleanupListeners);
    }
    this.mutationObserver?.disconnect();
  }  

  render() {
    return (
      <Host>
        <div class={`radio ${this.disabled ? 'radio--disabled' : ''} ${this.checked ? 'radio--checked' : ''}`}>
          <label htmlFor={this.inputId} class="radio__label" part="label" id={`${this.inputId}-label`}>
            <input
              ref={el => (this.input = el)}
              id={this.inputId}
              class={`radio__input ${this.error ? 'radio__input--error' : ''} ${this.disabled ? 'radio__input--disabled' : ''}`}
              name={`radio-${this.name}`}
              type="radio"
              required={this.required}
              value={this.value}
              aria-labelledby={`${this.inputId}-label`}
              aria-describedby={getFormFieldId(this.el) ? `${getFormFieldId(this.el)}-help` : null}
              disabled={this.disabled}
              onChange={this.onChange}
              checked={this.checked}
              part="input"
              {...this.inheritedAttributes}
            />
            {this.hasTextSlot && (
              <div class="radio__text" part="text">
                <slot></slot>
              </div>
            )}
          </label>
        </div>
      </Host>
    );
  }
}
